// Unified API client combining public and authenticated API functionality
import appConfig from '../config/appConfig';

class UnifiedApiClient {
  constructor() {
    // Use appConfig with environment fallbacks, ensure no trailing slash
    let baseURL = appConfig.apiBaseUrl || 
      import.meta.env.REACT_APP_API_BASE_URL || 
      import.meta.env.REACT_APP_API_URL || 
      'http://localhost:5131';
    
    this.baseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
    
    this.tokenProvider = null;
    this.onUnauthorized = null;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
    this.debugEnabled = import.meta.env.REACT_APP_ENABLE_API_DEBUG === 'true';
    
    // Simple request cache to prevent duplicate requests
    this.requestCache = new Map();
    this.cacheTimeout = 5000; // 5 seconds
  }

  // Invalidate cached GET responses whose URL contains the given path fragment
  clearCacheFor(pathFragment) {
    for (const key of this.requestCache.keys()) {
      if (key.includes(pathFragment)) {
        this.requestCache.delete(key);
      }
    }
  }

  // Set the token provider function
  setTokenProvider(fn) {
    this.tokenProvider = fn;
  }

  // Set callback for 401 unauthorized responses
  setOnUnauthorized(fn) {
    this.onUnauthorized = fn;
  }

  // Get current token from provider
  async getToken() {
    if (!this.tokenProvider) return null;
    
    try {
      return await this.tokenProvider();
    } catch (error) {
      console.warn('Failed to get token from provider:', error);
      return null;
    }
  }

  // Extract subdomain from hostname for X-Tenant-Subdomain header
  getSubdomain() {
    if (typeof window === 'undefined') return null;
    
    const hostname = window.location.hostname;
    
    // For localhost development - extract subdomain from patterns like basoccer.localhost
    if (hostname.includes('.localhost')) {
      const parts = hostname.split('.');
      return parts[0]; // Returns 'basoccer' from 'basoccer.localhost'
    }
    
    // For plain localhost
    if (hostname === 'localhost') {
      return 'localhost';
    }
    
    // For subdomains like basoccer.quailgame.com
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      return parts[0]; // Returns 'basoccer', 'platform', etc.
    }
    
    return null;
  }

  // Build full URL from endpoint
  buildUrl(endpoint) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseURL}/${cleanEndpoint}`;
  }

  // Get headers with authentication and tenant info
  // When skipDefaultContentType is true, the default JSON Content-Type header
  // will be omitted (useful for FormData/file uploads where the browser
  // should set the boundary-aware Content-Type header automatically).
  async getHeaders(customHeaders = {}, options = {}) {
    const headers = { ...this.defaultHeaders, ...customHeaders };

    if (options.skipDefaultContentType) {
      delete headers['Content-Type'];
    }
    
    // Try to get token
    const token = await this.getToken();
    
    if (token) {
      // Authenticated request
      headers.Authorization = `Bearer ${token}`;
    } else {
      // Anonymous request - add tenant subdomain header
      const subdomain = this.getSubdomain();
      if (subdomain) {
        headers['X-Tenant-Subdomain'] = subdomain;
      }
    }
    
    return headers;
  }

  // Generic request method with JSON handling, 404 → null (unless options.throwOnNotFound), and abort/timeout support
  async request(endpoint, options = {}) {
    const url = this.buildUrl(endpoint);

    // Skip authenticated endpoints when no token is available to avoid 401 noise
    if (options.requiresAuth) {
      const token = await this.getToken();
      if (!token) return null;
    }
    
    // Check cache for GET requests (only cache GET requests)
    const isGetRequest = !options.method || options.method === 'GET';
    const cacheKey = `${options.method || 'GET'}:${url}`;
    
    if (isGetRequest && this.requestCache.has(cacheKey)) {
      const cached = this.requestCache.get(cacheKey);
      const now = Date.now();
      
      if (now - cached.timestamp < this.cacheTimeout) {
        console.log(`🚀 Cache hit for ${endpoint}`);
        return cached.data;
      } else {
        // Remove expired cache entry
        this.requestCache.delete(cacheKey);
      }
    }
    
    const isFormDataBody = options.body instanceof FormData;

    const headers = await this.getHeaders(options.headers, {
      skipDefaultContentType: isFormDataBody,
    });
    
    const config = {
      method: 'GET',
      cache: 'no-store',
      ...options,
      headers,
      signal: options.signal, // Pass through AbortSignal if provided
    };

    // Add timeout wrapper for production (configurable, defaults to 10 seconds)
    const controller = new AbortController();
    const timeoutMs = options.timeoutMs ?? 10000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    // Use provided signal or timeout signal
    if (!config.signal) {
      config.signal = controller.signal;
    }

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      // Handle 404 specifically - return null (or reject when the caller must distinguish it from 204)
      if (response.status === 404) {
        if (options.throwOnNotFound) {
          throw new Error(`${config.method} ${endpoint} failed with status 404: Not Found`);
        }
        return null;
      }
      
      // Handle 401 Unauthorized - session expired or invalid
      if (response.status === 401) {
        if (this.onUnauthorized) {
          this.onUnauthorized();
        }
        throw new Error(`${config.method} ${endpoint} failed with status 401: Unauthorized`);
      }

      // Handle other non-200 status codes
      if (!response.ok) {
        const errorContentType = response.headers.get('content-type') || '';
        let errorMessage = null;

        if (errorContentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = (errorData && typeof errorData === 'object' && errorData.message)
              ? errorData.message
              : JSON.stringify(errorData);
          } catch {
            // JSON parsing failed — fall through to text
          }
        }

        if (!errorMessage) {
          try {
            errorMessage = await response.text();
          } catch {
            // text parsing failed
          }
        }

        throw new Error(errorMessage || `Request failed with status ${response.status}`);
      }

      // Any successful mutation can make previously cached GET responses stale.
      if (!isGetRequest) {
        this.requestCache.clear();
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return null;
      }

      // Handle response body parsing
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      let responseData;
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else if (!contentType && contentLength && parseInt(contentLength) > 0) {
        // Missing content-type but has body - try JSON first, fall back to text
        try {
          responseData = await response.json();
        } catch {
          responseData = await response.text();
        }
      } else {
        responseData = await response.text();
      }
      
      // Cache successful GET responses
      if (isGetRequest && responseData !== null) {
        this.requestCache.set(cacheKey, {
          data: responseData,
          timestamp: Date.now()
        });
      }
      
      return responseData;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (this.debugEnabled) {
        console.error(`API request failed for ${config.method} ${endpoint}:`, error);
      }
      
      // Re-throw with enhanced error message
      if (error.name === 'AbortError') {
        throw new Error(`${config.method} ${endpoint} timed out`);
      }
      
      throw error;
    }
  }

  // Generic HTTP methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }

  // Domain helper methods mirroring publicApi functionality
  // All still use /public/ endpoints for now

  // Get organization information (tenant resolved by backend)
  async getOrganization() {
    return await this.get('/api/organization');
  }

  // Get league standings
  async getLeagueStandings(leagueSlug) {
    return await this.get(`/api/leagues/${leagueSlug}/standings`);
  }

  async rebuildLeagueStandings(leagueId) {
    return await this.post(`/api/standings/league/${leagueId}/rebuild`);
  }

  async getStandingsByLeagueId(leagueId) {
    return await this.get(`/api/standings/league/${leagueId}`);
  }

  // Get league schedule
  async getLeagueSchedule(leagueSlug) {
    return await this.get(`/api/leagues/${leagueSlug}/schedule`);
  }

  // Schedule placeholders
  async createSchedulePlaceholder(data) {
    return await this.post('/api/schedule-placeholders', data);
  }

  async updateSchedulePlaceholder(id, data) {
    return await this.put(`/api/schedule-placeholders/${id}`, data);
  }

  async deleteSchedulePlaceholder(id) {
    return await this.delete(`/api/schedule-placeholders/${id}`);
  }

  async convertSchedulePlaceholder(id, data) {
    return await this.post(`/api/schedule-placeholders/${id}/convert`, data);
  }

  // Schedule generator drafts (CanManageLeague)
  clearScheduleDraftCaches(leagueId) {
    this.clearCacheFor(`/api/leagues/${leagueId}/schedule-drafts`);
  }

  async getScheduleDrafts(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/schedule-drafts`);
  }

  async getScheduleDraft(leagueId, draftId) {
    return await this.get(`/api/leagues/${leagueId}/schedule-drafts/${draftId}`);
  }

  async createScheduleDraft(leagueId, dto) {
    const result = await this.post(`/api/leagues/${leagueId}/schedule-drafts`, dto, { timeoutMs: 60000 });
    this.clearScheduleDraftCaches(leagueId);
    return result;
  }

  async updateScheduleDraft(leagueId, draftId, dto) {
    const result = await this.put(`/api/leagues/${leagueId}/schedule-drafts/${draftId}`, dto, { timeoutMs: 60000 });
    this.clearScheduleDraftCaches(leagueId);
    return result;
  }

  async regenerateScheduleDraft(leagueId, draftId) {
    const result = await this.post(`/api/leagues/${leagueId}/schedule-drafts/${draftId}/regenerate`, undefined, { timeoutMs: 60000 });
    this.clearScheduleDraftCaches(leagueId);
    return result;
  }

  async approveScheduleDraft(leagueId, draftId, { replaceExistingGames = false } = {}) {
    const result = await this.post(
      `/api/leagues/${leagueId}/schedule-drafts/${draftId}/approve`,
      { replaceExistingGames },
      { timeoutMs: 120000 },
    );
    this.clearScheduleDraftCaches(leagueId);
    this.clearCacheFor(`/api/leagues/${leagueId}`);
    this.clearCacheFor(`/api/games/league/${leagueId}`);
    this.clearCacheFor('/api/games');
    this.clearCacheFor('/api/schedule-placeholders');
    return result;
  }

  async deleteScheduleDraft(leagueId, draftId) {
    const result = await this.delete(`/api/leagues/${leagueId}/schedule-drafts/${draftId}`);
    this.clearScheduleDraftCaches(leagueId);
    return result;
  }

  // Reusable weekly slot templates for the schedule generator (tenant-scoped)
  async getScheduleSlotTemplates() {
    return await this.get('/api/schedule-slot-templates');
  }

  async createScheduleSlotTemplate(dto) {
    const result = await this.post('/api/schedule-slot-templates', dto);
    this.clearCacheFor('/api/schedule-slot-templates');
    return result;
  }

  async updateScheduleSlotTemplate(id, dto) {
    const result = await this.put(`/api/schedule-slot-templates/${id}`, dto);
    this.clearCacheFor('/api/schedule-slot-templates');
    return result;
  }

  // Resolves null when the template was already deleted (404).
  async deleteScheduleSlotTemplate(id) {
    const result = await this.delete(`/api/schedule-slot-templates/${id}`);
    this.clearCacheFor('/api/schedule-slot-templates');
    return result;
  }

  // Get league cumulative points report
  async getLeagueCumulativePoints(leagueSlug) {
    return await this.get(`/api/leagues/${leagueSlug}/cumulative-points`);
  }

  // Get league teams list
  async getLeagueTeams(leagueSlug) {
    return await this.get(`/api/leagues/${leagueSlug}/teams`);
  }

  // Get league teams with managers (requires leagueId, returns TeamWithManagerDto[])
  async getLeagueTeamsWithManagers(leagueId) {
    return await this.get(`/api/teams/league/${leagueId}/with-managers`);
  }

  // Delete a team
  async deleteTeam(teamId) {
    return await this.delete(`/api/teams/${teamId}`);
  }

  // Get all teams in the tenant, optionally filtered by league
  async getTeams(leagueId = null) {
    const query = leagueId ? `?leagueId=${encodeURIComponent(leagueId)}` : '';
    return await this.get(`/api/teams${query}`);
  }

  async getTeamPlayers(teamId) {
    return await this.get(`/api/teams/${teamId}/players`);
  }

  async uploadTeamPlayersCsv(teamId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return await this.request(`/api/teams/${teamId}/players/upload`, {
      method: 'POST',
      body: formData,
      skipDefaultContentType: true
    });
  }

  async uploadTeamEventsCsv(teamId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return await this.request(`/api/teams/${teamId}/events/upload`, {
      method: 'POST',
      body: formData,
      skipDefaultContentType: true
    });
  }

  async previewTeamRosterImport(teamId, file) {
    const formData = new FormData();
    formData.append('file', file);
    try {
      return await this.request(`/api/teams/${teamId}/players/import/preview`, {
        method: 'POST',
        body: formData,
        skipDefaultContentType: true
      });
    } catch (err) {
      const text = err?.message ?? (typeof err === 'string' ? err : null);
      if (text) {
        const bodyMatch = text.match(/status 400: (.+)$/s);
        const responseBody = bodyMatch ? bodyMatch[1] : text;
        if (responseBody.trim().startsWith('{') || responseBody.trim().startsWith('[')) {
          try {
            return JSON.parse(responseBody);
          } catch { /* not JSON, fall through */ }
        }
      }
      throw err;
    }
  }

  async publishTeamRosterImport(teamId, rows) {
    return await this.post(`/api/teams/${teamId}/players/import/publish`, { rows });
  }

  // Get leading scorers for a league
  async getLeadingScorers(leagueSlug, options = {}) {
    const { limit = 10, minimumGoals = 0 } = options;
    const queryParams = new URLSearchParams();
    
    if (limit !== 10) queryParams.append('limit', limit.toString());
    if (minimumGoals !== 0) queryParams.append('minimumGoals', minimumGoals.toString());
    
    const queryString = queryParams.toString();
    const endpoint = `/api/leagues/${leagueSlug}/leading-scorers${queryString ? `?${queryString}` : ''}`;
    
    return await this.get(endpoint);
  }

  // Get clean sheet leaders for a league
  async getCleanSheets(leagueSlug, options = {}) {
    const { limit = 5, minimumCleanSheets = 0 } = options;
    const queryParams = new URLSearchParams();
    
    if (limit !== 5) queryParams.append('limit', limit.toString());
    if (minimumCleanSheets !== 0) queryParams.append('minimumCleanSheets', minimumCleanSheets.toString());
    
    const queryString = queryParams.toString();
    const endpoint = `/api/leagues/${leagueSlug}/clean-sheets${queryString ? `?${queryString}` : ''}`;
    
    return await this.get(endpoint);
  }

  // Get team details
  async getTeam(teamSlug) {
    return await this.get(`/api/teams/${teamSlug}`);
  }

  // Get basic team details (lightweight - just for navigation)
  async getTeamBasic(teamSlug) {
    return await this.get(`/api/teams/${teamSlug}/basic`);
  }

  // Get player profile by userTenantId
  async getPlayerProfile(userTenantId) {
    return await this.get(`/api/userTenant/${userTenantId}`);
  }

  // Get player statistics with 5-minute browser cache
  async getPlayerStatsCached(userTenantId) {
    const cacheKey = `playerStats:${userTenantId}`;
    const cached = this._longCache?.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 300000) {
      return cached.data;
    }
    const data = await this.get(`/api/userTenant/${userTenantId}/stats`);
    if (!this._longCache) this._longCache = new Map();
    if (data) {
      this._longCache.set(cacheKey, { data, timestamp: Date.now() });
    }
    return data;
  }

  // Update player profile
  async updatePlayerProfile(userTenantId, updateDto) {
    return await this.put(`/api/userTenant/${userTenantId}`, updateDto);
  }

  // Upload player profile photo
  // Returns { url: string } on success
  async uploadPlayerPhoto(userTenantId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    return await this.request(`/api/userTenant/${userTenantId}/photo`, {
      method: 'POST',
      body: formData,
      skipDefaultContentType: true
    });
  }

  // Get player registrations (Paid/Refunded only)
  async getPlayerRegistrations(userTenantId) {
    return await this.get(`/api/userTenant/${userTenantId}/registrations`);
  }

  async getLeagueProfilePhotos(leagueId) {
    return await this.get(`/api/reports/leagues/${leagueId}/profile-photos`);
  }

  async uploadLeagueProfilePhoto(leagueId, userTenantId, file) {
    const formData = new FormData();
    formData.append('file', file);

    const result = await this.request(`/api/reports/leagues/${leagueId}/profile-photos/${userTenantId}/upload`, {
      method: 'POST',
      body: formData,
      skipDefaultContentType: true
    });

    this.clearCacheFor(`/api/reports/leagues/${leagueId}/profile-photos`);
    return result;
  }

  async downloadLeagueProfilePhotosZip(leagueId) {
    return await this.getBlob(`/api/reports/leagues/${leagueId}/profile-photos/export`);
  }

  async downloadLeagueProfilePhoto(leagueId, userTenantId) {
    return await this.getBlob(`/api/reports/leagues/${leagueId}/profile-photos/${userTenantId}/download`);
  }

  // Get player event registrations
  async getPlayerEventRegistrations(userTenantId) {
    return await this.get(`/api/userTenant/${userTenantId}/event-registrations`);
  }

  // Get current user's event registrations
  async getMyEventRegistrations() {
    return await this.get('/api/me/event-registrations');
  }

  // Get public venues
  async getVenues() {
    return await this.get('/api/venues');
  }

  // Create a new venue
  async createVenue(venueData) {
    const result = await this.post('/api/venues', venueData);
    this.clearCacheFor('/api/venues');
    return result;
  }

  // Update a venue
  async updateVenue(venueId, venueData) {
    const result = await this.put(`/api/venues/${venueId}`, venueData);
    this.clearCacheFor('/api/venues');
    return result;
  }

  // Delete a venue
  async deleteVenue(venueId) {
    const result = await this.delete(`/api/venues/${venueId}`);
    this.clearCacheFor('/api/venues');
    return result;
  }

  // Create a field for a venue
  async createField(fieldData) {
    const result = await this.post('/api/fields', fieldData);
    this.clearCacheFor('/api/venues');
    return result;
  }

  // Delete a field
  async deleteField(fieldId) {
    const result = await this.delete(`/api/fields/${fieldId}`);
    this.clearCacheFor('/api/venues');
    return result;
  }

  // Get next N games for a league
  async getUpcomingGames(leagueSlug, limit = 4) {
    const schedule = await this.get(`/api/leagues/${leagueSlug}/schedule`);
    if (!schedule || !schedule.games) return [];
    
    const now = new Date();
    return schedule.games
      .filter(game => new Date(game.start) > now)
      .sort((a, b) => new Date(a.start) - new Date(b.start))
      .slice(0, limit);
  }

  // Download bulk game sheets for a team
  async downloadBulkGameSheets(teamSlug) {
    return await this.getBlob(`/api/games/teams/${teamSlug}/bulk-gamesheets`);
  }

  async downloadGameSheet(gameId, teamId) {
    return await this.getBlob(`/api/games/${gameId}/teams/${teamId}/gamesheet`);
  }

  async getGameById(gameId) {
    return await this.get(`/api/games/${gameId}`);
  }

  async getLeagueGames(leagueId) {
    return await this.get(`/api/games/league/${leagueId}`);
  }

  // Get leading scorers for a specific team
  async getTeamLeadingScorers(teamKey, limit = 10, minimumGoals = 0) {
    const params = new URLSearchParams();
    if (limit !== 10) params.append('limit', limit.toString());
    if (minimumGoals !== 0) params.append('minimumGoals', minimumGoals.toString());
    
    const queryString = params.toString();
    const endpoint = `/api/teams/${teamKey}/leading-scorers${queryString ? `?${queryString}` : ''}`;
    return await this.get(endpoint);
  }

  // Get match event types
  async getMatchEventTypes() {
    return await this.get('/api/games/event-types');
  }

  // Spare pool management methods (one spare pool per league)
  async getLeagueSparePool(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/spare-pool`);
  }

  async getSparePoolMembers(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/spares/members`);
  }

  async createSparePoolMember(leagueId, createDto) {
    return await this.post(`/api/leagues/${leagueId}/spares/members`, createDto);
  }

  async updateSparePoolMember(memberId, updateDto) {
    return await this.put(`/api/spare/members/${memberId}`, updateDto);
  }

  async deleteSparePoolMember(memberId) {
    return await this.delete(`/api/spare/members/${memberId}`);
  }

  async uploadSparePoolMembersCsv(leagueId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return await this.request(`/api/leagues/${leagueId}/spares/members/upload`, {
      method: 'POST',
      body: formData,
      skipDefaultContentType: true
    });
  }

  // ============================================
  // RSVP (Token-based)
  // ============================================

  /**
   * Submit an RSVP response by token (anonymous access, one-click)
   * @param {string} token - The RSVP token (GUID)
   * @param {string} choice - The RSVP choice (e.g. 'yes', 'no')
   * @returns {Promise<{status: string, attending: boolean, message: string}>}
   */
  async respondToRsvp(token, choice) {
    return await this.post(`/api/rsvp/respond/${token}/${choice}`);
  }

  /**
   * Get all player RSVPs for a game. Games are events, so the game ID is the event ID.
   * @param {number} gameId - The game (event) ID
   * @returns {Promise<Array|null>} Array of RsvpDto (or null on 404): { id, eventId, userTenantId, attending, respondedDate }
   */
  async getGameRsvps(gameId) {
    return await this.get(`/api/rsvps/event/${gameId}`);
  }

  /**
   * Create or update a player's RSVP for a game (the endpoint upserts by event + userTenantId)
   * @param {number} eventId - The game (event) ID
   * @param {number} userTenantId - The player's UserTenant ID
   * @param {boolean} attending - Whether the player is attending
   * @returns {Promise<Object|null>} RsvpDto (or null on 404)
   */
  async upsertGameRsvp(eventId, userTenantId, attending) {
    return await this.post('/api/rsvps', { eventId, userTenantId, attending });
  }

  // ============================================
  // Spare Availability Methods
  // ============================================

  /**
   * Get the availability matrix for all members of a league's spare pool
   * @param {number} leagueId - The league ID
   * @param {object} options - Optional filters: { startDate, endDate }
   * @returns {Promise<SpareAvailabilityMatrixDto>}
   */
  async getSpareAvailabilityMatrix(leagueId, options = {}) {
    const params = new URLSearchParams();
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    const query = params.toString();
    return await this.get(`/api/leagues/${leagueId}/spares/availability-matrix${query ? `?${query}` : ''}`);
  }

  /**
   * Set a spare pool member's availability
   * @param {number} memberId - The spare pool member ID
   * @param {Array} availabilities - Array of { spareTimeSlotId, isAvailable }
   * @returns {Promise<null>} Resolves on 204; rejects on 404 (member or slot no longer exists) and other errors
   */
  async setSparePoolMemberAvailability(memberId, availabilities) {
    return await this.put(`/api/spare/members/${memberId}/availability`, { availabilities }, { throwOnNotFound: true });
  }

  /**
   * Get spare availability by token (anonymous access)
   * @param {string} token - The availability token (GUID)
   * @param {string} timezone - Optional timezone for date display
   * @returns {Promise<SpareAvailabilityTokenResponseDto>}
   */
  async getSpareAvailabilityByToken(token, timezone = null) {
    const params = timezone ? `?timezone=${encodeURIComponent(timezone)}` : '';
    return await this.get(`/api/spare-availability/${token}${params}`);
  }

  /**
   * Submit spare availability responses by token (anonymous access)
   * @param {string} token - The availability token (GUID)
   * @param {Array} availabilities - Array of { spareTimeSlotId, isAvailable }
   * @returns {Promise<{status: string, message: string}>}
   */
  async submitSpareAvailabilityByToken(token, availabilities) {
    return await this.post(`/api/spare-availability/${token}`, { availabilities });
  }

  // ============================================
  // Spare Coordinator Emails
  // ============================================

  /**
   * Get the current user's saved Reply-To / CC for spare emails (defaults to their own email)
   * @param {number} leagueId - The league ID
   * @returns {Promise<{replyToEmail: string|null, ccEmail: string|null}>}
   */
  async getSpareEmailSettings(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/spare-emails/settings`);
  }

  /**
   * Preview spares matching the availability filters for a game date
   * @param {number} leagueId - The league ID
   * @param {object} filter - { gameDate: 'YYYY-MM-DD', statuses: [], goalkeepersOnly, includeExempt }
   * @returns {Promise<{recipientCount: number, recipients: Array}>}
   */
  async previewSpareEmail(leagueId, filter) {
    return await this.post(`/api/leagues/${leagueId}/spare-emails/preview`, filter);
  }

  /**
   * Send (or schedule via scheduledSendDateTime in org timezone) an email to filtered spares
   * @param {number} leagueId - The league ID
   * @param {object} request - Filter + { subject, htmlBody, replyToEmail, ccEmail, scheduledSendDateTime }
   * @returns {Promise<{batchId: number, status: string, recipientCount: number, scheduledSendAt: string}>}
   */
  async sendSpareEmail(leagueId, request) {
    return await this.post(`/api/leagues/${leagueId}/spare-emails/send`, request);
  }

  /**
   * Send a single [TEST] copy of a spare email
   * @param {number} leagueId - The league ID
   * @param {object} request - { gameDate, subject, htmlBody, replyToEmail, testEmail }
   */
  async sendSpareEmailTest(leagueId, request) {
    return await this.post(`/api/leagues/${leagueId}/spare-emails/test`, request);
  }

  /**
   * Get sent and scheduled spare emails for a league (newest first)
   * @param {number} leagueId - The league ID
   * @returns {Promise<Array>} Array of SpareEmailBatchSummaryDto
   */
  async getSpareEmailHistory(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/spare-emails`);
  }

  /**
   * Get a spare email with per-recipient delivery status
   * @param {number} leagueId - The league ID
   * @param {number} batchId - The spare email batch ID
   * @returns {Promise<SpareEmailBatchDetailDto>}
   */
  async getSpareEmailBatch(leagueId, batchId) {
    return await this.get(`/api/leagues/${leagueId}/spare-emails/${batchId}`);
  }

  /**
   * Cancel a scheduled spare email that has not been sent yet
   * @param {number} leagueId - The league ID
   * @param {number} batchId - The spare email batch ID
   */
  async cancelSpareEmail(leagueId, batchId) {
    return await this.delete(`/api/leagues/${leagueId}/spare-emails/${batchId}`);
  }

  // ============================================
  // Player Availability (Token-based)
  // ============================================

  /**
   * Get player game availability by token (anonymous access)
   * @param {string} token - The availability token (GUID)
   * @param {string} timezone - Optional timezone for date display
   * @returns {Promise<PlayerAvailabilityTokenResponseDto>}
   */
  async getPlayerAvailabilityByToken(token, timezone = null) {
    const params = timezone ? `?timezone=${encodeURIComponent(timezone)}` : '';
    return await this.get(`/api/player-availability/${token}${params}`);
  }

  /**
   * Submit player availability responses by token (anonymous access)
   * @param {string} token - The availability token (GUID)
   * @param {Array} availabilities - Array of { gameId, isAvailable }
   * @param {Array} spareAvailabilities - Array of { sparePoolMemberId, spareTimeSlotId, isAvailable }
   * @returns {Promise<{status: string, message: string}>}
   */
  async submitPlayerAvailabilityByToken(token, availabilities, spareAvailabilities = []) {
    return await this.post(`/api/player-availability/${token}`, { availabilities, spareAvailabilities });
  }

  /**
   * Submit a social event RSVP via the player availability token
   * @param {string} token - The availability token (GUID)
   * @param {number} socialEventId - The social event id
   * @param {boolean} attending - true to attend, false to decline
   * @returns {Promise<{status: string, message: string}>}
   */
  async submitSocialEventRsvpViaPlayerToken(token, socialEventId, attending) {
    return await this.post(`/api/player-availability/${token}/social-event/${socialEventId}`, { attending });
  }

  /**
   * Get current user's profile information
   * @returns {Promise<UserDto>}
   */
  async getMe() {
    return await this.get('/api/me', { requiresAuth: true });
  }

  /**
   * Get current user's roles and manageable resources. Bypasses the request
   * cache so explicit refreshes always see the latest permissions.
   * @returns {Promise<UserPermissionsDto|null>} null when no token is available
   */
  async getMyPermissions() {
    this.clearCacheFor('/api/me/permissions');
    return await this.get('/api/me/permissions', { requiresAuth: true });
  }

  /**
   * Get current user's team memberships in active leagues
   * @returns {Promise<MyTeamDto[]>}
   */
  async getMyTeams() {
    return await this.get('/api/me/teams', { requiresAuth: true });
  }

  /**
   * Get current user's home page data (next games + league groupings)
   * @returns {Promise<MyHomeDto>}
   */
  async getMyHome() {
    return await this.get('/api/me/home', { requiresAuth: true });
  }

  /**
   * Get current user's availability token info (determines player vs spare)
   * @returns {Promise<MyAvailabilityInfoDto>}
   */
  async getMyAvailabilityToken() {
    return await this.get('/api/me/availability-token');
  }

  /**
   * Get pending match reports for the current user's manageable teams
   * @returns {Promise<PendingMatchReportDto[]>}
   */
  async getPendingMatchReports() {
    return await this.get('/api/me/pending-match-reports');
  }

  // Get both teams' match reports and reconciliation status for a game
  async getGameMatchReports(gameId) {
    return await this.get(`/api/games/${gameId}/reports`);
  }

  async submitMatchReport(gameId, payload, requestId) {
    return await this.post(`/api/games/${gameId}/reports`, payload, {
      headers: { 'X-Request-ID': requestId },
    });
  }

  /**
   * Update an existing match report (league admin+ only)
   * @param {number} gameId
   * @param {number} reportId
   * @param {object} payload
   * @returns {Promise<object>}
   */
  async updateMatchReport(gameId, reportId, payload) {
    return await this.put(`/api/games/${gameId}/reports/${reportId}`, payload);
  }

  /**
   * Get match report conflicts for a league
   * @param {number} leagueId
   * @returns {Promise<MatchReportSummaryDto[]>}
   */
  async getMatchReportConflicts(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/match-report-conflicts`);
  }

  /**
   * Get all submitted match reports in a league (admin browse view)
   * @param {number} leagueId
   * @param {object} [filters]
   * @param {number} [filters.teamId]
   * @param {string} [filters.status] - any|submitted|overridden|conflict|finalized|awaiting-other
   * @param {string} [filters.from] - ISO date
   * @param {string} [filters.to] - ISO date
   * @returns {Promise<MatchReportSummaryDto[]>}
   */
  async getLeagueMatchReports(leagueId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.teamId) params.append('teamId', filters.teamId);
    if (filters.status) params.append('status', filters.status);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const qs = params.toString();
    return await this.get(`/api/leagues/${leagueId}/match-reports${qs ? `?${qs}` : ''}`);
  }

  /**
   * Get count of match report conflicts across all manageable leagues
   * @returns {Promise<{totalConflicts: number}>}
   */
  async getMatchReportConflictCount() {
    return await this.get('/api/me/match-report-conflict-count');
  }

  // Admin methods for league management
  async getLeagueById(leagueId) {
    return await this.get(`/api/leagues/${leagueId}`);
  }

  async getLeagueBySlug(leagueSlug) {
    return await this.get(`/api/leagues/${encodeURIComponent(leagueSlug)}`);
  }

  // ============================================
  // League Draft
  // ============================================

  async getLeagueDraft(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/draft/players`);
  }

  async getLeagueDraftViewers(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/draft/viewers`);
  }

  async searchLeagueDraftViewerCandidates(leagueId, query) {
    return await this.get(`/api/leagues/${leagueId}/draft/viewer-candidates?query=${encodeURIComponent(query)}`);
  }

  async addLeagueDraftViewer(leagueId, userTenantId) {
    return await this.post(`/api/leagues/${leagueId}/draft/viewers`, { userTenantId });
  }

  async removeLeagueDraftViewer(leagueId, userTenantId) {
    return await this.delete(`/api/leagues/${leagueId}/draft/viewers/${userTenantId}`);
  }

  // Super Admin: manually rebuild precomputed draft statistics for the current tenant.
  // Uses an extended timeout since aggregation can exceed the default 10s on large tenants.
  async recalculateDraftStats() {
    return await this.post('/api/admin/draft-stats/recalculate', undefined, { timeoutMs: 120000 });
  }

  // Super Admin: manual one-time game reminder emails.
  async getManualReminderGameDates(leagueId) {
    return await this.get(`/api/admin/game-reminders/leagues/${leagueId}/dates`);
  }

  async previewManualGameReminder(leagueId, gameDate, reminderType, audience = 'Players') {
    const params = new URLSearchParams({ leagueId, gameDate, reminderType, audience });
    return await this.get(`/api/admin/game-reminders/preview?${params.toString()}`);
  }

  // Sends now when scheduledSendDateTime is null; otherwise schedules (organization timezone).
  async sendManualGameReminder({ leagueId, gameDate, reminderType, audience = 'Players', scheduledSendDateTime = null }) {
    return await this.post(
      '/api/admin/game-reminders/send',
      { leagueId, gameDate, reminderType, audience, scheduledSendDateTime },
      { timeoutMs: 120000 },
    );
  }

  async getManualGameReminders() {
    return await this.get('/api/admin/game-reminders');
  }

  async cancelManualGameReminder(id) {
    return await this.delete(`/api/admin/game-reminders/${id}`);
  }

  async getLeagueStats(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/stats`);
  }

  async updateLeague(leagueId, updateDto) {
    return await this.put(`/api/leagues/${leagueId}`, updateDto);
  }

  async getLeagues() {
    return await this.get('/api/leagues');
  }

  async createLeague(createDto) {
    return await this.post('/api/leagues', createDto);
  }

  async deleteLeague(leagueId) {
    return await this.delete(`/api/leagues/${leagueId}`);
  }

  async getLeagueAttendanceReport(leagueId) {
    return await this.get(`/api/playerstatistics/attendance-report/league/${leagueId}`);
  }

  async getLeagueDisciplinaryReport(leagueId) {
    return await this.get(`/api/playerstatistics/disciplinary-report/league/${leagueId}`);
  }

  // ============================================
  // Registration Management
  // ============================================

  async getRegistrationConfig(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/registration-config`);
  }

  async upsertRegistrationConfig(leagueId, configDto) {
    return await this.put(`/api/leagues/${leagueId}/registration-config`, configDto);
  }

  async generateEarlyAccessLink(leagueId, expiresAt = null) {
    return await this.post(`/api/leagues/${leagueId}/registration-config/early-access`, { expiresAt });
  }

  async revokeEarlyAccessLink(leagueId) {
    return await this.delete(`/api/leagues/${leagueId}/registration-config/early-access`);
  }

  async getRegistrations(leagueId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.positionType != null) params.append('positionType', filters.positionType);
    if (filters.status != null) params.append('status', filters.status);
    if (filters.isNewToLeague != null) params.append('isNewToLeague', filters.isNewToLeague);
    const query = params.toString();
    return await this.get(`/api/leagues/${leagueId}/registrations${query ? `?${query}` : ''}`);
  }

  async getRegistrationById(leagueId, registrationId) {
    return await this.get(`/api/leagues/${leagueId}/registrations/${registrationId}`);
  }

  async getRegistrationInventory(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/registrations/inventory`);
  }

  async getRegistrationsExport(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/registrations/export`);
  }

  async refundRegistration(leagueId, registrationId, removeMembership = false) {
    return await this.post(`/api/leagues/${leagueId}/registrations/${registrationId}/refund`, { removeMembership });
  }

  // Edit a registration (change type/positions, override amount paid). CanManageLeague.
  async updateRegistration(leagueId, registrationId, updateDto) {
    const result = await this.put(`/api/leagues/${leagueId}/registrations/${registrationId}`, updateDto);
    this.clearCacheFor(`/api/leagues/${leagueId}/registrations`);
    this.clearCacheFor(`/api/leagues/${leagueId}/registrations/inventory`);
    return result;
  }

  async markExpiredRegistrationPaid(leagueId, registrationId) {
    return await this.post(`/api/leagues/${leagueId}/registrations/${registrationId}/expired/mark-paid`, {});
  }

  async deleteExpiredRegistration(leagueId, registrationId) {
    return await this.delete(`/api/leagues/${leagueId}/registrations/${registrationId}/expired`);
  }

  // ============================================
  // Organization Events
  // ============================================

  async getEvents() {
    return await this.get('/api/org-events');
  }

  async getEvent(eventId) {
    return await this.get(`/api/org-events/${eventId}`);
  }

  async createEvent(data) {
    const result = await this.post('/api/org-events', data);
    this.clearCacheFor('/api/org-events');
    return result;
  }

  async updateEvent(eventId, data) {
    const result = await this.put(`/api/org-events/${eventId}`, data);
    this.clearCacheFor('/api/org-events');
    this.clearCacheFor(`/api/org-events/${eventId}`);
    return result;
  }

  async deleteEvent(eventId) {
    const result = await this.delete(`/api/org-events/${eventId}`);
    this.clearCacheFor('/api/org-events');
    this.clearCacheFor(`/api/org-events/${eventId}`);
    return result;
  }

  async getEventRegistrations(eventId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status != null) params.append('status', filters.status);
    if (filters.isExistingPlayer != null) params.append('isExistingPlayer', filters.isExistingPlayer);
    const query = params.toString();
    return await this.get(`/api/org-events/${eventId}/registrations${query ? `?${query}` : ''}`);
  }

  async getEventRegistration(eventId, registrationId) {
    return await this.get(`/api/org-events/${eventId}/registrations/${registrationId}`);
  }

  async getEventRegistrationInventory(eventId) {
    return await this.get(`/api/org-events/${eventId}/registrations/inventory`);
  }

  async exportEventRegistrations(eventId) {
    return await this.getBlob(`/api/org-events/${eventId}/registrations/export`);
  }

  async refundEventRegistration(eventId, registrationId) {
    const result = await this.post(`/api/org-events/${eventId}/registrations/${registrationId}/refund`);
    this.clearCacheFor(`/api/org-events/${eventId}/registrations`);
    this.clearCacheFor(`/api/org-events/${eventId}/registrations/inventory`);
    return result;
  }

  async markEventRegistrationPaid(eventId, registrationId) {
    const result = await this.post(`/api/org-events/${eventId}/registrations/${registrationId}/mark-paid`);
    this.clearCacheFor(`/api/org-events/${eventId}/registrations`);
    this.clearCacheFor(`/api/org-events/${eventId}/registrations/inventory`);
    return result;
  }

  async deleteEventRegistration(eventId, registrationId) {
    const result = await this.delete(`/api/org-events/${eventId}/registrations/${registrationId}`);
    this.clearCacheFor(`/api/org-events/${eventId}/registrations`);
    this.clearCacheFor(`/api/org-events/${eventId}/registrations/inventory`);
    return result;
  }

  // ============================================
  // Reminder Logs
  // ============================================

  async getReminderBatchSummaries(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/reminder-logs`);
  }

  async getReminderGameDetails(leagueId, gameId, reminderType) {
    const params = reminderType != null ? `?reminderType=${reminderType}` : '';
    return await this.get(`/api/leagues/${leagueId}/reminder-logs/${gameId}${params}`);
  }

  async getWaiverText() {
    return await this.get('/api/tenant/waiver');
  }

  async updateWaiverText(waiverText) {
    return await this.put('/api/tenant/waiver', { waiverText });
  }

  // Feature switches: { organization: { spares: true }, leagues: [{ leagueId, overrides: { spares: false } }] }
  async getFeatures() {
    return await this.get('/api/features');
  }

  async updateOrganizationFeatures(features) {
    const result = await this.put('/api/tenant/features', { features });
    this.clearCacheFor('/api/features');
    return result;
  }

  // features: { spares: true | false | null } — null removes the league override (follow the organization).
  async updateLeagueFeatures(leagueId, features) {
    const result = await this.put(`/api/leagues/${leagueId}/features`, { features });
    this.clearCacheFor('/api/features');
    return result;
  }

  // Scoring rules: { regularSeasonScoring, playoffScoring, regularSeasonOptions, playoffOptions }
  async getScoringRules() {
    return await this.get('/api/tenant/scoring-rules');
  }

  // Changing a type rebuilds every league's standings, so cached standings and points charts are dropped too.
  async updateScoringRules({ regularSeasonScoring, playoffScoring }) {
    const result = await this.put(
      '/api/tenant/scoring-rules',
      { regularSeasonScoring, playoffScoring },
      { timeoutMs: 120000 },
    );
    ['/api/tenant/scoring-rules', 'standings', '/cumulative-points'].forEach((path) => this.clearCacheFor(path));
    return result;
  }

  // Organization settings (public): name, subdomain, logoUrl, timezone and display preferences.
  async getTenantSettings() {
    return await this.get('/api/Tenant/settings');
  }

  async getTenantStats() {
    return await this.get('/api/Tenant/stats');
  }

  // ============================================
  // Invitation Management
  // ============================================

  /**
   * Get all organization members with invitation status
   * @returns {Promise<Array>} Array of OrganizationMemberDto with invitation fields
   */
  async getOrganizationMembers() {
    return await this.get('/api/UserTenant/members');
  }

  /**
   * Add a new member to the organization
   * @param {{ firstName: string, lastName: string, email: string, sendInvitation: boolean }} memberData
   * @returns {Promise<{ userTenantId: number, message: string, invitationSent: boolean }>}
   */
  async addOrganizationMember(memberData) {
    return await this.post('/api/organization/members', memberData);
  }

  /**
   * Send an invitation email to a user
   * @param {number} userTenantId - The UserTenant ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async sendInvitationEmail(userTenantId) {
    return await this.post(`/api/invitations/${userTenantId}/send-email`);
  }

  /**
   * Generate an invitation link that can be copied and shared
   * @param {number} userTenantId - The UserTenant ID
   * @returns {Promise<{invitationUrl: string, email: string, message: string}>}
   */
  async generateInvitationLink(userTenantId) {
    return await this.post(`/api/invitations/${userTenantId}/generate-link`);
  }

  /**
   * Generate a one-time-use Auth0 password reset URL for a member
   * @param {number} userTenantId - The UserTenant ID
   * @returns {Promise<{url: string}>}
   */
  async generatePasswordResetUrl(userTenantId) {
    return await this.post(`/api/UserTenant/${userTenantId}/password-reset-url`);
  }

  /**
   * Send a branded password reset email to a member via ACS
   * @param {number} userTenantId - The UserTenant ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async sendPasswordResetEmail(userTenantId) {
    return await this.post(`/api/UserTenant/${userTenantId}/password-reset-email`);
  }

  // Validate an invitation token (anonymous). Returns null when invalid or expired.
  async validateInvitation(token) {
    return await this.get(`/api/invitations/validate/${encodeURIComponent(token)}`);
  }

  /**
   * Get count of league members eligible for bulk invitation
   * @param {number} leagueId - The league ID
   * @returns {Promise<{outstandingCount: number}>}
   */
  async getOutstandingInvitationCount(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/invitations/outstanding-count`);
  }

  /**
   * Send invitation emails to all outstanding league members
   * @param {number} leagueId - The league ID
   * @returns {Promise<{queuedCount: number}>}
   */
  async sendAllLeagueInvitations(leagueId) {
    return await this.post(`/api/leagues/${leagueId}/invitations/send-all`);
  }

  // Get blob data (e.g., PDFs, images) with proper authentication
  async getBlob(endpoint, options = {}) {
    const url = this.buildUrl(endpoint);
    const headers = await this.getHeaders(options.headers);
    
    const config = {
      method: 'GET',
      ...options,
      headers,
      signal: options.signal, // Pass through AbortSignal if provided
    };

    // Add timeout wrapper for production (10 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    // Use provided signal or timeout signal
    if (!config.signal) {
      config.signal = controller.signal;
    }

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      // Handle 404 specifically - return null
      if (response.status === 404) {
        return null;
      }
      
      // Handle 401 Unauthorized - session expired or invalid
      if (response.status === 401) {
        if (this.onUnauthorized) {
          this.onUnauthorized();
        }
        throw new Error(`${config.method} ${endpoint} failed with status 401: Unauthorized`);
      }

      // Handle other non-200 status codes
      if (!response.ok) {
        const errorText = await response.text();
        const errorMsg = `${config.method} ${endpoint} failed with status ${response.status}: ${errorText}`;
        throw new Error(errorMsg);
      }

      return await response.blob();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (this.debugEnabled) {
        console.error(`API blob request failed for ${config.method} ${endpoint}:`, error);
      }
      
      // Re-throw with enhanced error message
      if (error.name === 'AbortError') {
        throw new Error(`${config.method} ${endpoint} timed out`);
      }
      
      throw error;
    }
  }
  // ============================================
  // Public Registration Methods
  // ============================================

  async getPublicRegistrationConfig(leagueSlug, earlyAccess = null) {
    const query = earlyAccess ? `?earlyAccess=${encodeURIComponent(earlyAccess)}` : '';
    return await this.get(`/api/leagues/${leagueSlug}/register/config${query}`);
  }

  async submitRegistration(leagueSlug, data, earlyAccess = null) {
    const query = earlyAccess ? `?earlyAccess=${encodeURIComponent(earlyAccess)}` : '';
    return await this.post(`/api/leagues/${leagueSlug}/register${query}`, data);
  }

  async confirmRegistration(data) {
    return await this.post('/api/registration/confirm', data);
  }

  async getRegistrationLanding() {
    return await this.get('/api/registration/landing');
  }

  // ============================================
  // Public Event Registration Methods
  // ============================================

  async getPublicEvents() {
    return await this.get('/api/registration/events');
  }

  async getPublicEventRegistrationConfig(eventSlug) {
    return await this.get(`/api/org-events/${eventSlug}/register`);
  }

  async submitEventRegistration(eventSlug, data) {
    return await this.post(`/api/org-events/${eventSlug}/register`, data);
  }

  async confirmEventRegistration(data) {
    return await this.post('/api/registration/confirm-event', data);
  }

  // ============================================
  // Playoff Methods
  // ============================================

  async getPlayoffConfig(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/playoff-config`);
  }

  async upsertPlayoffConfig(leagueId, data) {
    return await this.put(`/api/leagues/${leagueId}/playoff-config`, data);
  }

  async getPlayoffPools(leagueId) {
    return await this.get(`/api/leagues/${leagueId}/playoff-config/pools`);
  }

  async setPlayoffPoolTeams(leagueId, poolId, teamIds) {
    return await this.put(`/api/leagues/${leagueId}/playoff-config/pools/${poolId}/teams`, { teamIds });
  }

  async removeTeamFromPool(leagueId, poolId, teamId) {
    return await this.delete(`/api/leagues/${leagueId}/playoff-config/pools/${poolId}/teams/${teamId}`);
  }

  async getPlayoffStandings(leagueKey) {
    return await this.get(`/api/leagues/${leagueKey}/playoff-standings`);
  }

  async recalculatePlayoffStandings(leagueId) {
    return await this.post(`/api/leagues/${leagueId}/playoff-standings/recalculate`);
  }

  async getPlayoffBracket(leagueSlug) {
    return await this.get(`/api/leagues/${leagueSlug}/playoff-bracket`);
  }

  async getPublicPlayoffConfig(leagueKey) {
    return await this.get(`/api/leagues/${leagueKey}/playoff-config/public`);
  }

  // ============================================
  // Game CSV Upload Methods
  // ============================================

  async uploadGamesCsv(leagueId, file) {
    const formData = new FormData();
    formData.append('csvFile', file);
    try {
      return await this.request(`/api/Games/league/${leagueId}/upload-csv`, {
        method: 'POST',
        body: formData,
        skipDefaultContentType: true
      });
    } catch (err) {
      // The upload endpoint returns 400 with a structured GameCsvUploadResultDto
      // body when validation fails. request() throws with the body as a string,
      // so try to parse it back into the structured result for the caller.
      const text = err?.message ?? (typeof err === 'string' ? err : null);
      if (text) {
        // Extract the response body after the "status 400: " prefix
        const bodyMatch = text.match(/status 400: (.+)$/s);
        if (bodyMatch) {
          try {
            return JSON.parse(bodyMatch[1]);
          } catch { /* not JSON, fall through */ }
        }
      }
      throw err;
    }
  }

  // ============ User Merge Methods ============

  async searchUsersForMerge(query) {
    return await this.get(`/api/user-merge/search?query=${encodeURIComponent(query)}`);
  }

  async findLikelyDuplicates() {
    return await this.get('/api/user-merge/find-duplicates');
  }

  async previewUserMerge(survivorUserTenantId, victimUserTenantId) {
    return await this.post('/api/user-merge/preview', { survivorUserTenantId, victimUserTenantId });
  }

  async executeUserMerge(survivorUserTenantId, victimUserTenantId) {
    return await this.post('/api/user-merge/execute', { survivorUserTenantId, victimUserTenantId });
  }

  // ============ Bulk Email Methods ============

  async sendBulkEmail(request) {
    return await this.post('/api/bulk-email/send', request);
  }

  async sendTestEmail(request) {
    return await this.post('/api/bulk-email/test', request);
  }

  async uploadEmailAttachment(file) {
    const formData = new FormData();
    formData.append('file', file);
    return await this.request('/api/bulk-email/upload-attachment', {
      method: 'POST',
      body: formData,
      skipDefaultContentType: true,
    });
  }

  async deleteEmailAttachment(url) {
    return await this.request(`/api/bulk-email/attachment?url=${encodeURIComponent(url)}`, {
      method: 'DELETE',
    });
  }

  // ============ Reports Methods ============

  async getSeasonComparison(currentLeagueId, previousLeagueId) {
    return await this.get(`/api/reports/season-comparison?currentLeagueId=${currentLeagueId}&previousLeagueId=${previousLeagueId}`);
  }

  // ============ Social Events Methods ============

  async listTenantSocialEvents(includePast = false) {
    return await this.get(`/api/social-events?includePast=${includePast}`);
  }

  async getSocialEvent(id) {
    return await this.get(`/api/social-events/${id}`);
  }

  async getSocialEventRsvps(id) {
    return await this.get(`/api/social-events/${id}/rsvps`);
  }

  async createSocialEvent(data) {
    return await this.post('/api/social-events', data);
  }

  async updateSocialEvent(id, data) {
    return await this.put(`/api/social-events/${id}`, data);
  }

  async deleteSocialEvent(id) {
    return await this.delete(`/api/social-events/${id}`);
  }

  async rsvpToSocialEvent(id, attending) {
    return await this.post(`/api/social-events/${id}/rsvp`, { attending });
  }

  async getSocialEventByToken(token) {
    return await this.get(`/api/social-event-rsvp/${token}`);
  }

  async submitSocialEventTokenRsvp(token, attending) {
    return await this.post(`/api/social-event-rsvp/${token}`, { attending });
  }

  /**
   * Get info for a photo-upload token (validates the token and returns the recipient's name).
   * @param {string} token - The photo upload token (GUID)
   * @returns {Promise<{firstName: string, lastName: string, expiresAt: string}>}
   */
  async getPhotoUploadTokenInfo(token) {
    return await this.get(`/api/photo-upload/${token}`);
  }

  /**
   * Upload a profile photo via a one-time token (no login required).
   * @param {string} token - The photo upload token (GUID)
   * @param {File} file - The image file to upload
   * @returns {Promise<{status: string, url: string}>}
   */
  async uploadPhotoViaToken(token, file) {
    const formData = new FormData();
    formData.append('file', file);
    return await this.request(`/api/photo-upload/${token}`, {
      method: 'POST',
      body: formData,
    });
  }

  // Emergency contact: self or CanViewAdminData can read; only self can update.
  async getEmergencyContact(userTenantId) {
    return await this.get(`/api/usertenant/${userTenantId}/emergency-contact`);
  }

  async updateEmergencyContact(userTenantId, data) {
    return await this.put(`/api/usertenant/${userTenantId}/emergency-contact`, data);
  }

  async getEmergencyContactTokenInfo(token) {
    return await this.get(`/api/emergency-contact/${token}`);
  }

  async submitEmergencyContactViaToken(token, data) {
    return await this.post(`/api/emergency-contact/${token}`, data);
  }

  // Outstanding (overdue) payment-provider invoice balances.
  async getMyBalance() {
    return await this.get('/api/me/balance');
  }

  async getMemberBalance(userTenantId) {
    return await this.get(`/api/usertenant/${userTenantId}/balance`);
  }

  async getOutstandingBalances({ leagueId, teamId, spareStatus } = {}) {
    const params = new URLSearchParams();
    if (leagueId) params.append('leagueId', leagueId);
    if (teamId) params.append('teamId', teamId);
    if (spareStatus) params.append('spareStatus', spareStatus);
    const query = params.toString();
    return await this.get(`/api/outstanding-balances${query ? `?${query}` : ''}`);
  }

  // Full sync from the payment provider; throttled server-side to once every 5 minutes.
  async syncOutstandingBalances() {
    return await this.post('/api/outstanding-balances/sync', undefined, { timeoutMs: 120000 });
  }

  // ============================================
  // Team Chat

  // Channels the current user belongs to, with a hasNewMessages flag.
  async getChatChannels() {
    return await this.get('/api/chat/channels');
  }

  // Latest messages (oldest first); pass `before` (a message id) to page backwards.
  async getChatMessages(channelId, { before, take } = {}) {
    const params = new URLSearchParams();
    if (before) params.append('before', before);
    if (take) params.append('take', take);
    const query = params.toString();
    return await this.get(`/api/chat/channels/${channelId}/messages${query ? `?${query}` : ''}`);
  }

  // Organization members you can message directly (name + photo only); needs 2+ characters.
  async searchChatMembers(query) {
    return await this.get(`/api/chat/direct/candidates?query=${encodeURIComponent(query)}`);
  }

  // Everyone in a chat channel (first and last name only, unsorted); members only, null when not found/allowed.
  async getChatChannelMembers(channelId) {
    this.clearCacheFor(`/api/chat/channels/${channelId}/members`);
    return await this.get(`/api/chat/channels/${channelId}/members`);
  }

  // Opens (creating on first use) the direct conversation with another member; returns the channel.
  async openDirectChat(userTenantId) {
    return await this.post('/api/chat/direct', { userTenantId });
  }

  async sendChatMessage(channelId, content) {
    return await this.post(`/api/chat/channels/${channelId}/messages`, { content });
  }

  async deleteChatMessage(messageId) {
    return await this.delete(`/api/chat/messages/${messageId}`);
  }

  // Edits the text of the caller's own message; returns the updated message (null when it no longer exists).
  async editChatMessage(messageId, content) {
    return await this.put(`/api/chat/messages/${messageId}`, { content });
  }

  // Marks the channel read up to `lastMessageId` (the newest message displayed), or fully when omitted.
  async markChatChannelSeen(channelId, lastMessageId) {
    return await this.post(`/api/chat/channels/${channelId}/seen`, lastMessageId ? { lastMessageId } : {});
  }

  // Removes a direct conversation from the caller's chat list until a newer message arrives (messages are kept).
  async hideChatChannel(channelId) {
    return await this.post(`/api/chat/channels/${channelId}/hide`, {});
  }

  // Photo + optional caption. Large photos are accepted and resized server-side, so allow slow uploads.
  async sendChatPhoto(channelId, file, caption) {
    const formData = new FormData();
    formData.append('photo', file);
    if (caption) formData.append('caption', caption);
    return await this.request(`/api/chat/channels/${channelId}/messages/photo`, {
      method: 'POST',
      body: formData,
      timeoutMs: 120000,
    });
  }

  // GIPHY GIFs for the chat picker (20 per page): trending when `query` is blank, otherwise search results.
  // Returns { gifs: [{ id, title, width, height, url, previewUrl }], hasMore, nextOffset }; throws (503) when GIPHY is unavailable.
  async getChatGifs({ query = '', offset = 0 } = {}) {
    const params = new URLSearchParams();
    const trimmed = query.trim();
    if (trimmed) params.append('q', trimmed);
    if (offset) params.append('offset', offset);
    const qs = params.toString();
    return await this.get(`/api/chat/gifs/${trimmed ? 'search' : 'trending'}${qs ? `?${qs}` : ''}`);
  }

  // Sends a GIPHY GIF (by id) with an optional caption; the server looks up the GIF's details.
  async sendChatGif(channelId, gifId, caption) {
    return await this.post(`/api/chat/channels/${channelId}/messages/gif`, { gifId, caption: caption || null });
  }

  // Private chat photo as a Blob (use URL.createObjectURL); `thumbnail` loads the small version.
  async getChatAttachment(attachmentId, { thumbnail = false } = {}) {
    return await this.getBlob(`/api/chat/attachments/${attachmentId}${thumbnail ? '?size=thumb' : ''}`);
  }

  async addChatReaction(messageId, emoji) {
    return await this.put(`/api/chat/messages/${messageId}/reactions`, { emoji });
  }

  // Chat push notifications
  async getChatPushConfig() {
    return await this.get('/api/chat/notifications/config');
  }

  // `subscription` is PushSubscription.toJSON(): { endpoint, keys: { p256dh, auth } }
  async saveChatPushSubscription(subscription) {
    return await this.put('/api/chat/notifications/subscriptions', {
      endpoint: subscription.endpoint,
      keys: subscription.keys,
    });
  }

  async removeChatPushSubscription(endpoint) {
    return await this.post('/api/chat/notifications/subscriptions/remove', { endpoint });
  }

  async getChatNotificationPreferences() {
    return await this.get('/api/chat/notifications/preferences');
  }

  async updateChatNotificationPreferences(preferences) {
    return await this.put('/api/chat/notifications/preferences', preferences);
  }

  async setChatChannelMuted(channelId, muted) {
    return await this.put(`/api/chat/channels/${channelId}/mute`, { muted });
  }

  async removeChatReaction(messageId, emoji) {
    return await this.delete(`/api/chat/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`);
  }

  // Chat channel settings (channel admins, League Managers, Tenant/Super Admins).
  // Settings mutations also change admin listings and the member channel list (name/description).
  clearChatAdminCaches(channelId) {
    if (channelId) this.clearCacheFor(`/api/chat/channels/${channelId}/settings`);
    this.clearCacheFor('/api/chat/channels');
    this.clearCacheFor('/api/admin/chat-channels');
    this.clearCacheFor('/api/admin/chat/leagues');
  }

  async getChatChannelSettings(channelId) {
    this.clearCacheFor(`/api/chat/channels/${channelId}/settings`);
    return await this.get(`/api/chat/channels/${channelId}/settings`);
  }

  async updateChatChannelSettings(channelId, { name, description }) {
    const result = await this.put(`/api/chat/channels/${channelId}/settings`, { name, description });
    this.clearChatAdminCaches(channelId);
    return result;
  }

  async addChatChannelAdmin(channelId, userTenantId) {
    const result = await this.post(`/api/chat/channels/${channelId}/admins`, { userTenantId });
    this.clearChatAdminCaches(channelId);
    return result;
  }

  async removeChatChannelAdmin(channelId, userTenantId) {
    const result = await this.delete(`/api/chat/channels/${channelId}/admins/${userTenantId}`);
    this.clearChatAdminCaches(channelId);
    return result;
  }

  // Chat admin area (Super Admin, Tenant Admin, League Manager — scoped server-side)
  async getAdminChatLeagues() {
    this.clearCacheFor('/api/admin/chat/leagues');
    return await this.get('/api/admin/chat/leagues');
  }

  // enableChat=true also creates any missing league/team channels (idempotent).
  async setLeagueChatEnabled(leagueId, enableChat) {
    const result = await this.put(`/api/admin/chat/leagues/${leagueId}`, { enableChat });
    this.clearChatAdminCaches();
    return result;
  }

  async getAdminChatChannels({ leagueId, type, status } = {}) {
    const params = new URLSearchParams();
    if (leagueId) params.append('leagueId', leagueId);
    if (type) params.append('type', type);
    if (status) params.append('status', status);
    const query = params.toString();
    this.clearCacheFor('/api/admin/chat-channels');
    return await this.get(`/api/admin/chat-channels${query ? `?${query}` : ''}`);
  }

  async updateChatChannel(channelId, { isEnabled }) {
    const result = await this.request(`/api/admin/chat-channels/${channelId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isEnabled }),
    });
    this.clearChatAdminCaches(channelId);
    return result;
  }
}

// Create singleton instance
const unifiedApi = new UnifiedApiClient();

// Export both the instance and the setTokenProvider function for convenience
export const setTokenProvider = (fn) => unifiedApi.setTokenProvider(fn);
export const setOnUnauthorized = (fn) => unifiedApi.setOnUnauthorized(fn);

// Absolute URL for API-hosted assets (e.g. logos) used in src/href attributes.
export const buildApiUrl = (endpoint) => {
  if (!endpoint) return '';
  if (endpoint.startsWith('http')) return endpoint;
  return `${unifiedApi.baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
};

export default unifiedApi;
